import { useState } from 'react';

import { cx } from '@emotion/css';
import { Col, Form, Space } from 'antd';
import { Formik } from 'formik';
import { isEmpty, isUndefined, throttle } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { doClearGetQuote, doClearGetQuoteChart } from 'src/actions';
import { getClientApi } from 'src/data-communication';
import { MButton, MFormSelect, MModalBody, MModalFooter, MModalTitle } from 'src/lib';
import { AlignItems, BaseStyle, Display, JustifyContent, Spacing } from 'src/styles';
import * as Yup from 'yup';

import { SecurityDetailsCarousel } from './SecurityDetailsCarousel';

const clientApi = getClientApi();

const validatonSchema = Yup.object({
  symbol: Yup.string().required('Symbol/Company Name is required'),
  description: Yup.string(),
});

interface SecurityHoldingNewTradeSearchFormValues {
  symbol: string;
  description: string;
}

interface SecurityHoldingNewTradeSearchStepProps {
  symbol?: string;
  description?: string;
  onBuy: ({ symbol, description }: { symbol: string; description: string }) => void;
  onClose: () => void;
}

export const SecurityHoldingNewTradeSearchStep = ({
  symbol = '',
  description = '',
  onBuy,
  onClose,
}: SecurityHoldingNewTradeSearchStepProps) => {
  const [searchResult, setSearchResult] = useState<{ value: string; label: string }[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const authToken: string = useSelector((state: any) => state.auth.data.authToken);

  const dispatch = useDispatch();

  return (
    <Formik<SecurityHoldingNewTradeSearchFormValues>
      initialValues={{ symbol, description }}
      validateOnChange
      validationSchema={validatonSchema}
      onSubmit={values => {
        onBuy(values);
      }}>
      {form => {
        const onSearch = async (value: string) => {
          setIsSearching(true);

          try {
            if (!value) {
              return;
            }
            const response = await clientApi.symbol.search({
              queryParams: {
                query: value,
              },
              authToken,
            });

            const searchResult = response.data.map(item => ({
              value: item.name,
              label: `${item.description} (${item.name})`,
            }));

            setSearchResult(searchResult);
            form.setFieldError('symbol', undefined);
          } catch (error: any) {
            form.setFieldError(
              'symbol',
              `An unexpected error occurred. Please try again or contact clientservices@myipo.com for assistance. ${
                error.message ? `(Error: ${error?.message})` : ''
              }`,
            );
            setSearchResult([]);
          } finally {
            setIsSearching(false);
          }
        };

        return (
          <Form autoComplete='off' style={{ width: '100%' }}>
            <MModalTitle>Add New Security Buy Order</MModalTitle>
            <MModalBody height={600}>
              <Space direction='vertical' size={24} style={{ width: '100%' }}>
                <Col span={24} className={Spacing.mt24}>
                  <MFormSelect
                    label='Search By Symbol/Company Name'
                    placeholder='Enter'
                    showSearch
                    allowClear
                    showArrow={false}
                    defaultValue={symbol}
                    value={form.values.symbol}
                    options={
                      isEmpty(searchResult) && !isEmpty(form.values.symbol)
                        ? [
                            {
                              value: form.values.symbol,
                              label: `${form.values.description} (${form.values.symbol})`,
                            },
                          ]
                        : searchResult
                    }
                    filterOption={false}
                    onChange={value => {
                      form.setFieldValue('symbol', value);

                      if (isUndefined(value)) {
                        setSearchResult([]);
                        dispatch(doClearGetQuote());
                        dispatch(doClearGetQuoteChart());

                        return;
                      }
                    }}
                    onSearch={throttle(onSearch, 250)}
                    onSelect={async (value, option) => {
                      await form.setFieldValue('symbol', value, true);
                      form.setFieldValue('description', option.label.replace(` (${value})`, ''));
                    }}
                    error={form.errors.symbol}
                    loading={isSearching}
                    align='vertical'
                    testId={'search-by-symbol'}
                  />
                </Col>
                <Col span={24}>
                  <SecurityDetailsCarousel symbol={form.values.symbol} />
                </Col>
              </Space>
            </MModalBody>
            <MModalFooter>
              <Col span={24} className={cx(Display.flex, AlignItems.center, JustifyContent.spaceBetween)}>
                <MButton type='tertiary' onClick={onClose}>
                  Close
                </MButton>
                <Col className={BaseStyle.levelRight}>
                  <MButton disabled={!form.isValid} onClick={form.submitForm}>
                    Buy
                  </MButton>
                </Col>
              </Col>
            </MModalFooter>
          </Form>
        );
      }}
    </Formik>
  );
};
